<template>
    <div class="paper-maker-container">
        <div class="main-content">
            <div class="header-section">
                <div class="title">
                    <i class="fas fa-file-alt"></i> 试卷篮
                </div>
                <div class="actions">
                    <div v-if="questions.length > 0" class="action-btn toggle-btn" @click="toggleAllAnswers">
                        <i :class="allExpanded ? 'fas fa-angle-double-up' : 'fas fa-angle-double-down'"></i>
                        {{ allExpanded ? '收起全部解析' : '展开全部解析' }}
                    </div>
                    <div class="action-btn clear-btn" @click="clearBasket">
                        <i class="fas fa-trash-alt"></i> 清空试卷篮
                    </div>
                    <div class="action-btn download-btn" @click="downloadPaper('docx')">
                        <i class="fas fa-download"></i> 生成试卷 (DOCX)
                    </div>
                    <!-- PPT 导出功能暂未上线 -->
                    <div v-if="false" class="action-btn ppt-btn" @click="downloadPaper('ppt')">
                        <i class="fas fa-file-powerpoint"></i> 导出 PPT 课件
                    </div>
                </div>
            </div>

            <div v-if="questions.length > 0" class="basket-summary">
                <span class="summary-item"><i class="fas fa-layer-group"></i> 共 {{ questions.length }} 题</span>
            </div>

            <div class="questions-list">
                <div v-if="questions.length === 0" class="empty-state">
                    <i class="fas fa-inbox"></i>
                    <p>试卷篮是空的，快去添加题目吧！</p>
                    <div class="go-back-btn" @click="$router.push('/zujuan')">
                        去选题
                    </div>
                </div>

                <div v-else>
                    <!-- Grouped View -->
                    <div v-for="(group, gIndex) in groupedQuestions" :key="gIndex" class="question-group">
                        <div class="group-header" v-if="group.title">
                            {{ group.title }}
                        </div>

                        <!-- 分组由题型/知识点推导得出，跨分组拖拽无法保持，因此只允许组内排序 -->
                        <draggable v-model="group.list" item-key="id" handle=".drag-handle" animation="200"
                            @end="onDragEnd">
                            <template #item="{ element: question, index }">
                                <div class="question-card" :id="'q-' + question.id"
                                    :class="{ 'is-open': question.showAnswer }">
                                    <div class="drag-handle">
                                        <i class="fas fa-grip-vertical"></i>
                                    </div>
                                    <div class="question-main">
                                        <div class="question-meta">
                                            <span class="index-badge">第 {{ getGlobalIndex(question.id) }} 题</span>
                                            <div class="meta-item">
                                                <i class="fas fa-tag"></i> {{ question.typeName }}
                                            </div>
                                            <div class="meta-item" :class="'difficulty-' + question.difficultName">
                                                <i class="fas fa-chart-line"></i> {{ question.difficultName }}
                                            </div>
                                            <div class="meta-item kp-item"
                                                v-if="question.knowledgePoints && question.knowledgePoints.length">
                                                <i class="fas fa-book"></i> {{ question.knowledgePoints[0].name }}
                                            </div>
                                        </div>

                                        <div class="question-content" @click="toggleAnswer(question)">
                                            <md-editor preview-only preview-theme="github"
                                                :model-value="question.content"
                                                style="text-align: left;" />
                                            <div v-if="isChoiceQuestion(question)">
                                                <div :class="getLayoutClass(question)" class="option-container">
                                                    <div v-for="(option, idx) in getOptions(question)" :key="idx"
                                                        class="option-item-wrapper">
                                                        <span class="option-label">{{ option.label }}.</span>
                                                        <div class="option-content">
                                                            <md-editor preview-only preview-theme="github"
                                                                :model-value="option.content"
                                                                style="text-align: left; background: transparent;" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <el-collapse-transition>
                                            <div v-show="question.showAnswer" class="answer-section">
                                                <div class="answer-title">
                                                    <i class="fas fa-check-circle"></i> 答案
                                                </div>
                                                <div class="answer-content">
                                                    <md-editor v-if="question.answer" preview-only preview-theme="github"
                                                        :model-value="question.answer" style="text-align: left;" />
                                                    <span v-else class="answer-empty">暂无答案</span>
                                                </div>
                                                <div class="answer-title"
                                                    v-if="question.analysis || question.explanation">
                                                    <i class="fas fa-lightbulb"></i> 解析
                                                </div>
                                                <div class="answer-content" v-if="question.analysis">
                                                    <md-editor preview-only preview-theme="github"
                                                        :model-value="question.analysis" style="text-align: left;" />
                                                </div>
                                                <div class="answer-content" v-if="question.explanation">
                                                    <md-editor preview-only preview-theme="github"
                                                        :model-value="question.explanation" style="text-align: left;" />
                                                </div>
                                                <div class="answer-empty" v-if="!question.analysis && !question.explanation">
                                                    暂无解析
                                                </div>
                                                <div class="answer-title" v-if="question.keyPoints">
                                                    <i class="fas fa-key"></i> 关键点
                                                </div>
                                                <div class="answer-content" v-if="question.keyPoints">
                                                    <md-editor preview-only preview-theme="github"
                                                        :model-value="question.keyPoints" style="text-align: left;" />
                                                </div>
                                            </div>
                                        </el-collapse-transition>

                                        <div class="card-actions">
                                            <div class="remove-btn" @click.stop="removeQuestion(question.id)">
                                                <i class="fas fa-times"></i> 移除
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </draggable>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Sidebar -->
        <div class="sidebar">
            <div class="sidebar-header">
                分组与排序
            </div>

            <div class="sidebar-controls">
                <div class="control-group">
                    <el-radio-group v-model="groupMode" size="small" @change="handleSortOrGroupChange">
                        <el-radio-button label="type">按题型</el-radio-button>
                        <el-radio-button label="kp">按知识点</el-radio-button>
                        <el-radio-button label="added">按加入顺序</el-radio-button>
                    </el-radio-group>
                </div>

                <div class="control-group">
                    <el-radio-group v-model="sortMode">
                        <el-radio label="added">加入顺序</el-radio>
                        <el-radio label="difficulty">由易到难</el-radio>
                    </el-radio-group>
                </div>

                <!-- Type Order Section (only shown when groupMode is 'type') -->
                <div class="control-group type-order-section" v-if="groupMode === 'type'">
                    <div class="type-order-label">题型顺序 <span class="hint">(可拖拽调整)</span></div>
                    <draggable v-model="typeOrder" item-key="name" handle=".type-drag-handle" animation="200"
                        @end="onTypeOrderChange">
                        <template #item="{ element: typeItem, index }">
                            <div class="type-order-item" v-if="getTypeCount(typeItem.name) > 0">
                                <span class="type-drag-handle"><i class="fas fa-grip-vertical"></i></span>
                                <span class="type-order-index">{{ getVisibleTypeIndex(typeItem.name) }}</span>
                                <span class="type-order-name">{{ typeItem.name }}</span>
                                <span class="type-order-count">{{ getTypeCount(typeItem.name) }}题</span>
                            </div>
                        </template>
                    </draggable>
                </div>
            </div>

            <div class="sidebar-grid">
                <div v-for="(group, gIndex) in groupedQuestions" :key="gIndex">
                    <div class="sidebar-group-title" v-if="group.title">{{ group.title }}</div>
                    <draggable v-model="group.list" item-key="id" animation="200" class="grid-container"
                        @end="onDragEnd">
                        <template #item="{ element: question }">
                            <div class="grid-item" @click="scrollToQuestion(question.id)">
                                {{ getGlobalIndex(question.id) }}
                            </div>
                        </template>
                    </draggable>
                </div>
            </div>
        </div>

        <!-- Export Options Dialog -->
        <el-dialog v-model="exportDialogVisible" title="导出设置" width="500px">
            <el-form :model="exportForm" label-width="100px">
                <el-form-item label="试卷标题">
                    <el-input v-model="exportForm.title" placeholder="请输入试卷标题" />
                </el-form-item>
                <el-form-item label="答案位置">
                    <el-radio-group v-model="exportForm.answerLocation">
                        <el-radio label="end_of_question">每题后</el-radio>
                        <el-radio label="end_of_paper">卷尾</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item label="显示来源">
                    <el-switch v-model="exportForm.showSource" />
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="exportDialogVisible = false">取消</el-button>
                    <el-button type="primary" @click="confirmExport" :loading="exportLoading">
                        确认导出 {{ exportForm.format === 'ppt' ? 'PPT' : 'Word' }}
                    </el-button>
                </span>
            </template>
        </el-dialog>
        <!-- Download Payment Dialog -->
        <DownloadPayDialog v-model="payDialogVisible" :paperName="exportForm.title"
            :customPaperData="pendingCustomPaperData" @success="handleDownloadSuccess" />
    </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from 'vue'
import draggable from 'vuedraggable'
import MdEditor from 'md-editor-v3';
import 'md-editor-v3/lib/style.css';
import { ElNotification } from 'element-plus'
import dayjs from 'dayjs'
import api, { getUser } from '../utils/api';
import DownloadPayDialog from '../components/DownloadPayDialog.vue';

const questions = ref([])

const currentUserId = computed(() => {
    const user = getUser()
    return user ? user.id : null
})

// Sidebar State
const groupMode = ref('type') // 'type', 'kp', 'added'
const sortMode = ref('added') // 'added', 'difficulty'

// Type ordering (customizable)
const typeOrder = ref([
    { name: '单选题' },
    { name: '多选题' },
    { name: '填空题' },
    { name: '解答题' },
    { name: '证明题' },
    { name: '应用题' }
])

// Export State
const exportDialogVisible = ref(false)
const exportLoading = ref(false)
const exportForm = reactive({
    title: '',
    answerLocation: 'end_of_paper',
    showSource: false,
    format: 'docx' // 'docx' or 'ppt'
})

// Payment State
const payDialogVisible = ref(false)
const pendingCustomPaperData = ref(null)

const groupedQuestions = computed(() => {
    if (!questions.value || questions.value.length === 0) return []

    let groups = []

    if (groupMode.value === 'type') {
        // In type mode, group by typeName but preserve the order from questions array
        // This allows cross-group dragging to persist
        const typeGroups = {}
        const typeOrderMap = new Map() // Track first appearance order

        questions.value.forEach((q, idx) => {
            const type = q.typeName || '其他'
            if (!typeGroups[type]) {
                typeGroups[type] = []
                typeOrderMap.set(type, idx)
            }
            typeGroups[type].push(q)
        })

        // Sort types by typeOrder setting, then by first appearance
        const orderedTypeNames = typeOrder.value.map(t => t.name)
        const sortedTypes = Object.keys(typeGroups).sort((a, b) => {
            const aIdx = orderedTypeNames.indexOf(a)
            const bIdx = orderedTypeNames.indexOf(b)
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
            if (aIdx !== -1) return -1
            if (bIdx !== -1) return 1
            return typeOrderMap.get(a) - typeOrderMap.get(b)
        })

        sortedTypes.forEach(t => {
            let list = typeGroups[t]
            // Apply sorting within the group only if difficulty sort is selected
            if (sortMode.value === 'difficulty') {
                list = [...list].sort((a, b) => a.difficult - b.difficult)
            }
            groups.push({ title: t, list: list })
        })
    } else if (groupMode.value === 'kp') {
        const kpGroups = {}
        const other = []
        questions.value.forEach(q => {
            if (q.knowledgePoints && q.knowledgePoints.length > 0) {
                const kp = q.knowledgePoints[0].name
                if (!kpGroups[kp]) kpGroups[kp] = []
                kpGroups[kp].push(q)
            } else {
                other.push(q)
            }
        })

        Object.keys(kpGroups).forEach(k => {
            let list = kpGroups[k]
            if (sortMode.value === 'difficulty') {
                list = [...list].sort((a, b) => a.difficult - b.difficult)
            }
            groups.push({ title: k, list: list })
        })
        if (other.length) {
            let list = other
            if (sortMode.value === 'difficulty') {
                list = [...list].sort((a, b) => a.difficult - b.difficult)
            }
            groups.push({ title: '未分类', list: list })
        }
    } else {
        let list = [...questions.value]
        if (sortMode.value === 'difficulty') {
            list = list.sort((a, b) => a.difficult - b.difficult)
        }
        groups.push({ title: '全部题目', list: list })
    }

    return groups
})

// 从当前分组结果中展平出真实的展示顺序
// 注意：拖拽会直接改动 groupedQuestions 派生出的分组数组，
// 此时 groupedQuestions 这个 computed 并不会失效，
// 因此必须实时读取 group.list，而不能依赖其它 computed 的缓存值。
const flattenGroups = () => {
    const result = []
    groupedQuestions.value.forEach(group => {
        result.push(...group.list)
    })
    return result
}

const orderedQuestions = computed(() => flattenGroups())

const questionIndexMap = computed(() => {
    const map = new Map()
    orderedQuestions.value.forEach((q, idx) => {
        map.set(q.id, idx + 1)
    })
    return map
})

onMounted(() => {
    loadBasket()
})

const loadBasket = async () => {
    if (!currentUserId.value) {
        return
    }
    try {
        const res = await api.get(`/api/basket?userId=${currentUserId.value}`)
        questions.value = (res.data || []).map(q => ({ ...q, showAnswer: false }))
    } catch (err) {
        console.error('Failed to load basket', err)
        ElNotification({
            title: 'Error',
            message: '加载试卷篮失败',
            type: 'error'
        })
    }
}

const syncBasket = async () => {
    if (!currentUserId.value) return
    try {
        const questionIds = questions.value.map(q => q.id)
        await api.post('/api/basket/sync', {
            userId: currentUserId.value,
            questionIds: questionIds
        })
    } catch (err) {
        console.error('Failed to sync basket', err)
    }
}

const removeQuestion = async (id) => {
    if (!currentUserId.value) return
    try {
        await api.post('/api/basket/remove', {
            userId: currentUserId.value,
            questionId: id
        })
        questions.value = questions.value.filter(q => q.id !== id)
        ElNotification({
            title: 'Success',
            message: '题目已移除',
            type: 'success'
        })
    } catch (err) {
        console.error('Failed to remove question', err)
    }
}

const clearBasket = async () => {
    if (!currentUserId.value) return
    try {
        await api.post('/api/basket/sync', {
            userId: currentUserId.value,
            questionIds: []
        })
        questions.value = []
        ElNotification({
            title: 'Success',
            message: '试卷篮已清空',
            type: 'success'
        })
    } catch (err) {
        console.error('Failed to clear basket', err)
    }
}

// ...

// Helper to find global index of a question (based on display order from groupedQuestions)
const getGlobalIndex = (id) => {
    return questionIndexMap.value.get(id) || 0
}

// Handle Drag End (Reorder)
const onDragEnd = () => {
    const newOrder = flattenGroups()
    const changed = newOrder.length !== questions.value.length ||
        newOrder.some((q, i) => q.id !== questions.value[i]?.id)

    questions.value = newOrder
    if (changed) syncBasket()
}

// Handle Sort/Group Change
const handleSortOrGroupChange = () => {
    // If sorting by difficulty, we reorder the main list
    if (sortMode.value === 'difficulty') {
        questions.value.sort((a, b) => a.difficult - b.difficult)
        syncBasket()
    }
    // If switching back to 'added', we don't necessarily revert to original added time,
    // but rather keep the current custom order.
}

// Get count of questions for a specific type
const getTypeCount = (typeName) => {
    return questions.value.filter(q => q.typeName === typeName).length
}

// Get the visible index for a type (only counting types with questions > 0)
const getVisibleTypeIndex = (typeName) => {
    let visibleIndex = 0
    for (const t of typeOrder.value) {
        if (getTypeCount(t.name) > 0) {
            visibleIndex++
            if (t.name === typeName) {
                return visibleIndex
            }
        }
    }
    return visibleIndex
}

// Handle Type Order Change (drag end)
const onTypeOrderChange = () => {
    // type order only affects local display grouping, no basket sync needed.
}

const scrollToQuestion = (id) => {
    const el = document.getElementById('q-' + id)
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
}

// 统一读取选项（兼容大小写字段），并规范化 label / content
const getOptions = (question) => {
    const raw = question.options || question.Options || []
    return raw.map(opt => ({
        label: opt.label || opt.Label || '',
        content: opt.content || opt.Content || ''
    }))
}

const isChoiceQuestion = (question) => {
    return question.type === 1 || question.type === 6 ||
        question.typeName === '单选题' || question.typeName === '多选题'
}

const getLayoutClass = (question) => {
    const options = getOptions(question)
    if (!options.length) return '';
    const totalLength = options.reduce((sum, opt) => sum + (opt.content ? opt.content.length : 0), 0)
    if (totalLength <= 40) {
        return 'layout-1-line';
    } else if (totalLength <= 80) {
        return 'layout-2-line';
    } else {
        return 'layout-4-line';
    }
}

// 展开 / 收起单题解析
const toggleAnswer = (question) => {
    question.showAnswer = !question.showAnswer
}

// 是否所有题目都已展开解析
const allExpanded = computed(() => {
    return questions.value.length > 0 && questions.value.every(q => q.showAnswer)
})

// 一键展开 / 收起全部解析
const toggleAllAnswers = () => {
    const target = !allExpanded.value
    questions.value.forEach(q => { q.showAnswer = target })
}

const downloadPaper = (format = 'docx') => {
    if (questions.value.length === 0) {
        ElNotification({
            title: 'Warning',
            message: '试卷篮为空，无法生成文件',
            type: 'warning'
        })
        return
    }

    exportForm.format = format
    exportForm.title = `高中数学${format === 'ppt' ? '课件' : '练习'} ${dayjs().format('YYYY-MM-DD')}`
    exportDialogVisible.value = true
}

const confirmExport = async () => {
    exportLoading.value = true
    try {
        // Close export settings dialog
        exportDialogVisible.value = false

        // Prepare data for payment dialog
        pendingCustomPaperData.value = {
            title: exportForm.title,
            questions: orderedQuestions.value.map(q => q.id)
        }

        // Show payment dialog
        payDialogVisible.value = true
    } catch (err) {
        console.error('准备导出失败:', err)
        ElNotification({
            title: 'Error',
            message: '准备导出失败',
            type: 'error'
        })
    } finally {
        exportLoading.value = false
    }
}

const downloadFile = (blobData, fileName) => {
    const blob = new Blob([blobData])
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

const handleDownloadSuccess = async ({ downloadToken }) => {
    exportLoading.value = true
    try {
        const data = {
            questions: orderedQuestions.value,
            title: exportForm.title,
            answer_location: exportForm.answerLocation,
            show_source: exportForm.showSource,
            group_mode: groupMode.value,
            type_order: typeOrder.value.map(t => t.name),
            downloadToken: downloadToken // Pass token to backend
        }

        let res;
        const endpoint = exportForm.format === 'ppt' ? '/api/downloadPpt' : '/api/downloadDocx';
        res = await api.post(endpoint, data, {
            responseType: 'blob',
        })

        const fileName = `${exportForm.title}.${exportForm.format === 'ppt' ? 'pptx' : 'docx'}`
        downloadFile(res.data, fileName)

        ElNotification({
            title: 'Success',
            message: `${exportForm.format === 'ppt' ? 'PPT 课件' : '试卷'}生成成功`,
            type: 'success'
        })
        payDialogVisible.value = false
    } catch (err) {
        console.error('下载失败:', err)
        ElNotification({
            title: 'Error',
            message: '生成试卷失败',
            type: 'error'
        })
    } finally {
        exportLoading.value = false
    }
}
</script>

<style scoped>
.paper-maker-container {
    display: flex;
    min-height: 100vh;
    background-color: #f5f7fa;
    width: 100%;
}

.main-content {
    flex: 1;
    padding: 20px;
    min-width: 0;
    /* Prevent flex item overflow */
}

.sidebar {
    width: 280px;
    min-width: 280px;
    background: white;
    border-left: 1px solid #ebeef5;
    display: flex;
    flex-direction: column;
    padding: 20px;
    overflow-y: auto;
    align-self: stretch;
    /* Match height with main content */
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.title {
    font-size: 24px;
    font-weight: bold;
    color: #2c3e50;
    display: flex;
    align-items: center;
    gap: 10px;
}

.title i {
    color: #409eff;
}

.actions {
    display: flex;
    gap: 15px;
}

.action-btn {
    padding: 10px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s;
}

.clear-btn {
    background-color: #fef0f0;
    color: #f56c6c;
    border: 1px solid #fbc4c4;
}

.clear-btn:hover {
    background-color: #fde2e2;
}

.download-btn {
    background-color: #ecf5ff;
    color: #409eff;
    border: 1px solid #d9ecff;
}

.download-btn:hover {
    background-color: #c6e2ff;
}

.ppt-btn {
    background-color: #fff1f0;
    color: #ff4d4f;
    border: 1px solid #ffa39e;
}

.ppt-btn:hover {
    background-color: #ffccc7;
}

.toggle-btn {
    background-color: #f4f4f5;
    color: #606266;
    border: 1px solid #dcdfe6;
}

.toggle-btn:hover {
    background-color: #e9e9eb;
    color: #409eff;
    border-color: #c6e2ff;
}

.basket-summary {
    display: flex;
    align-items: center;
    gap: 20px;
    margin: -8px 0 16px;
    padding: 10px 16px;
    background: #ecf5ff;
    border: 1px solid #d9ecff;
    border-radius: 6px;
    font-size: 13px;
    color: #409eff;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.empty-state {
    text-align: center;
    padding: 60px;
    background: white;
    border-radius: 8px;
    color: #909399;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 20px;
    color: #dcdfe6;
}

.go-back-btn {
    display: inline-block;
    margin-top: 20px;
    padding: 10px 25px;
    background-color: #409eff;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.3s;
}

.go-back-btn:hover {
    background-color: #66b1ff;
}

.question-group {
    margin-bottom: 30px;
}

.group-header {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
    color: #303133;
    border-left: 4px solid #409eff;
    padding-left: 10px;
}

.question-card {
    display: flex;
    background: white;
    margin-bottom: 8px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    overflow: hidden;
    transition: box-shadow 0.3s;
}

.question-card:hover {
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.1);
}

.drag-handle {
    width: 40px;
    background-color: #f5f7fa;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: move;
    color: #909399;
    border-right: 1px solid #ebeef5;
}

.drag-handle:hover {
    background-color: #e4e7ed;
    color: #606266;
}

.question-main {
    flex: 1;
    padding: 12px 16px;
    position: relative;
}

.question-meta {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 8px;
    font-size: 14px;
    color: #606266;
}

.index-badge {
    background-color: #409eff;
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 5px;
}

.difficulty-容易 {
    color: #67c23a;
}

.difficulty-较易 {
    color: #a0cfff;
}

.difficulty-适中 {
    color: #e6a23c;
}

.difficulty-较难 {
    color: #f56c6c;
}

.difficulty-困难 {
    color: #909399;
}

.kp-item {
    color: #409eff;
}

.question-content {
    margin-bottom: 15px;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.2s;
}

.question-content:hover {
    background: #f8fafc;
}

.option-container {
    margin-top: 10px;
    gap: 10px;
}

.option-item-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 5px;
}

.option-label {
    flex-shrink: 0;
    margin-right: 5px;
    white-space: nowrap;
    font-weight: 600;
    color: #303133;
}

.option-content {
    flex: 1;
    min-width: 0;
}

/* 让选项内的 Markdown 渲染与题号保持同一行 */
.option-content :deep(p) {
    margin: 0 !important;
    display: inline-block !important;
}

.option-content :deep(.md-editor),
.option-content :deep(.md-editor-preview-wrapper),
.option-content :deep(.md-editor-preview) {
    display: inline-block !important;
    padding: 0 !important;
    overflow: hidden !important;
    background: transparent !important;
    border: none !important;
    width: auto !important;
}

/* Answer & Analysis Section */
.answer-section {
    margin-top: 4px;
    margin-bottom: 14px;
    padding: 14px 16px;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    border-left: 3px solid #6366f1;
    border-radius: 4px 10px 10px 4px;
}

.answer-title {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    letter-spacing: 0.2px;
}

.answer-title i {
    color: #f59e0b;
    margin-right: 6px;
}

.answer-content + .answer-title {
    margin-top: 14px;
}

.answer-content {
    font-size: 14px;
    line-height: 1.75;
    color: #334155;
    margin-bottom: 12px;
}

.answer-content:last-child {
    margin-bottom: 0;
}

.answer-empty {
    color: #9ca3af;
    font-style: italic;
    font-size: 14px;
}

.answer-content :deep(.md-editor-preview-wrapper) {
    padding: 0;
}

.answer-content :deep(.md-editor-preview p) {
    margin: 0 !important;
}

.question-card.is-open {
    box-shadow: 0 4px 16px 0 rgba(64, 158, 255, 0.18);
}

/* Fix Markdown Table Overflow */
:deep(.md-editor-preview table) {
    display: block;
    width: 100%;
    overflow-x: auto;
    word-break: keep-all;
}

:deep(.md-editor-preview img) {
    max-width: min(100%, 420px) !important;
    width: auto !important;
    height: auto !important;
    max-height: 180px;
    object-fit: contain;
    display: block;
    margin: 6px 0;
    border-radius: 4px;
}

.layout-1-line {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
}

.layout-2-line {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 8px;
    column-gap: 16px;
}

.layout-4-line {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.card-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    border-top: 1px solid #ebeef5;
    padding-top: 10px;
}

.remove-btn {
    color: #f56c6c;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 5px;
}

.remove-btn:hover {
    text-decoration: underline;
}

/* Sidebar Styles */
.sidebar-header {
    font-size: 16px;
    font-weight: bold;
    color: #606266;
    margin-bottom: 20px;
}

.sidebar-controls {
    margin-bottom: 20px;
}

.control-group {
    margin-bottom: 15px;
}

.sidebar-group-title {
    font-size: 14px;
    font-weight: bold;
    color: #303133;
    margin: 10px 0;
}

.grid-container {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    margin-bottom: 15px;
}

.grid-item {
    aspect-ratio: 1;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #606266;
    transition: all 0.2s;
}

.grid-item:hover {
    border-color: #409eff;
    color: #409eff;
}

/* Type Order Section Styles */
.type-order-section {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #ebeef5;
}

.type-order-label {
    font-size: 14px;
    font-weight: 500;
    color: #606266;
    margin-bottom: 10px;
}

.type-order-label .hint {
    font-size: 12px;
    color: #909399;
    font-weight: normal;
}

.type-order-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f5f7fa;
    border-radius: 4px;
    margin-bottom: 6px;
    transition: all 0.2s;
}

.type-order-item:hover {
    background: #ecf5ff;
}

.type-drag-handle {
    cursor: move;
    color: #909399;
    padding: 2px;
}

.type-drag-handle:hover {
    color: #606266;
}

.type-order-index {
    width: 20px;
    height: 20px;
    background: #409eff;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
}

.type-order-name {
    flex: 1;
    font-size: 14px;
    color: #303133;
}

.type-order-count {
    font-size: 12px;
    color: #909399;
}
</style>
